import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { db } from '../firebase';
import { collection, addDoc, updateDoc, deleteDoc, doc, query, where, getDocs, orderBy } from 'firebase/firestore';
import {
  Calendar, Clock, MapPin, User, Phone, Mail, Plus, Search,
  ChevronLeft, ChevronRight, Check, X, Bell, Send, Trash2, Edit2,
  Home, FileText, AlertCircle, CheckCircle, Loader, MoreVertical,
  MessageSquare, Car, ClipboardList, Eye, ExternalLink
} from 'lucide-react';
import Modal from '../components/Modal';
import './OnSiteAppointments.css';

// Appointment status configurations
const appointmentStatuses = {
  scheduled: { label: 'Scheduled', color: '#b8926a', bg: '#faf6f1', icon: Calendar },
  confirmed: { label: 'Confirmed', color: '#2c5f4e', bg: '#e8f0ed', icon: CheckCircle },
  en_route: { label: 'En Route', color: '#8b5cf6', bg: '#f5f3ff', icon: Car },
  in_progress: { label: 'In Progress', color: '#2563eb', bg: '#eff6ff', icon: ClipboardList },
  completed: { label: 'Completed', color: '#16a34a', bg: '#f0fdf4', icon: Check },
  cancelled: { label: 'Cancelled', color: '#dc2626', bg: '#fef2f2', icon: X },
  no_show: { label: 'No Show', color: '#6b7280', bg: '#f3f4f6', icon: AlertCircle }
};

// Reminder schedule options
const reminderOptions = [
  { value: '1h', label: '1 hour before', hours: 1 },
  { value: '24h', label: '1 day before', hours: 24 },
  { value: '72h', label: '3 days before', hours: 72 },
  { value: '168h', label: '7 days before', hours: 168 }
];

// Time slot options
const timeSlots = [];
for (let h = 8; h <= 18; h++) {
  for (let m = 0; m < 60; m += 30) {
    const hour = h % 12 || 12;
    const ampm = h >= 12 ? 'PM' : 'AM';
    const time24 = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    const time12 = `${hour}:${m.toString().padStart(2, '0')} ${ampm}`;
    timeSlots.push({ value: time24, label: time12 });
  }
}

// Duration options
const durationOptions = [
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 45, label: '45 minutes' },
  { value: 60, label: '1 hour' },
  { value: 90, label: '1.5 hours' },
  { value: 120, label: '2 hours' }
];

function OnSiteAppointments() {
  const navigate = useNavigate();
  const { state } = useApp();
  const { clients, properties } = state;

  // State
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('week'); // 'day', 'week', 'list'
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modal states
  const [showNewModal, setShowNewModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // New appointment form
  const [formData, setFormData] = useState({
    clientId: '',
    propertyId: '',
    // New client fields (if no existing client selected)
    newClient: {
      firstName: '',
      lastName: '',
      email: '',
      phone: ''
    },
    // New property fields
    newProperty: {
      street: '',
      city: 'Charlottesville',
      state: 'VA',
      zip: ''
    },
    isNewClient: false,
    isNewProperty: false,
    // Appointment details
    date: new Date().toISOString().split('T')[0],
    time: '10:00',
    duration: 30,
    notes: '',
    internalNotes: '',
    // Reminders (all enabled by default)
    reminders: {
      '1h': true,
      '24h': true,
      '72h': true,
      '168h': true
    },
    reminderChannels: {
      email: true,
      sms: true
    }
  });

  // Fetch appointments from Firebase
  useEffect(() => {
    const fetchAppointments = async () => {
      try {
        const appointmentsRef = collection(db, 'onSiteAppointments');
        const q = query(appointmentsRef, orderBy('date', 'asc'));
        const snapshot = await getDocs(q);
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAppointments(data);
      } catch (error) {
        console.error('Error fetching appointments:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAppointments();
  }, []);

  // Get client properties
  const getClientProperties = (clientId) => {
    return properties.filter(p => p.clientId === clientId);
  };

  // Get client by ID
  const getClient = (clientId) => {
    return clients.find(c => c.id === clientId);
  };

  // Filter appointments
  const filteredAppointments = useMemo(() => {
    return appointments.filter(apt => {
      // Status filter
      if (statusFilter !== 'all' && apt.status !== statusFilter) return false;
      
      // Search filter
      if (searchQuery) {
        const search = searchQuery.toLowerCase();
        const clientName = `${apt.clientFirstName || ''} ${apt.clientLastName || ''}`.toLowerCase();
        const address = (apt.address || '').toLowerCase();
        const phone = (apt.clientPhone || '').toLowerCase();
        if (!clientName.includes(search) && !address.includes(search) && !phone.includes(search)) {
          return false;
        }
      }
      
      return true;
    });
  }, [appointments, statusFilter, searchQuery]);

  // Get appointments for selected date range
  const getAppointmentsForRange = (startDate, endDate) => {
    return filteredAppointments.filter(apt => {
      const aptDate = new Date(apt.date);
      return aptDate >= startDate && aptDate <= endDate;
    });
  };

  // Week navigation
  const getWeekDates = (date) => {
    const start = new Date(date);
    start.setDate(start.getDate() - start.getDay()); // Start of week (Sunday)
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      dates.push(d);
    }
    return dates;
  };

  const weekDates = getWeekDates(selectedDate);

  const navigateWeek = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setSelectedDate(newDate);
  };

  const navigateDay = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + direction);
    setSelectedDate(newDate);
  };

  // Format date for display
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatTime = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Handle form changes
  const updateForm = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateNewClient = (field, value) => {
    setFormData(prev => ({
      ...prev,
      newClient: { ...prev.newClient, [field]: value }
    }));
  };

  const updateNewProperty = (field, value) => {
    setFormData(prev => ({
      ...prev,
      newProperty: { ...prev.newProperty, [field]: value }
    }));
  };

  const toggleReminder = (reminderKey) => {
    setFormData(prev => ({
      ...prev,
      reminders: { ...prev.reminders, [reminderKey]: !prev.reminders[reminderKey] }
    }));
  };

  const toggleReminderChannel = (channel) => {
    setFormData(prev => ({
      ...prev,
      reminderChannels: { ...prev.reminderChannels, [channel]: !prev.reminderChannels[channel] }
    }));
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      clientId: '',
      propertyId: '',
      newClient: { firstName: '', lastName: '', email: '', phone: '' },
      newProperty: { street: '', city: 'Charlottesville', state: 'VA', zip: '' },
      isNewClient: false,
      isNewProperty: false,
      date: new Date().toISOString().split('T')[0],
      time: '10:00',
      duration: 30,
      notes: '',
      internalNotes: '',
      reminders: { '1h': true, '24h': true, '168h': true },
      reminderChannels: { email: true, sms: true }
    });
  };

  // Create appointment
  const handleCreateAppointment = async () => {
    setIsSaving(true);
    
    try {
      let clientId = formData.clientId;
      let clientData = {};
      let propertyId = formData.propertyId;
      let propertyData = {};

      // Create new client if needed
      if (formData.isNewClient) {
        const newClientDoc = await addDoc(collection(db, 'clients'), {
          firstName: formData.newClient.firstName,
          lastName: formData.newClient.lastName,
          email: formData.newClient.email,
          phone: formData.newClient.phone,
          status: 'lead',
          source: 'on_site_appointment',
          createdAt: new Date().toISOString(),
          notificationPrefs: {
            appointment: { email: true, sms: true }
          }
        });
        clientId = newClientDoc.id;
        clientData = formData.newClient;
      } else {
        const client = getClient(formData.clientId);
        clientData = {
          firstName: client?.firstName || '',
          lastName: client?.lastName || '',
          email: client?.email || '',
          phone: client?.phone || ''
        };
      }

      // Create new property if needed
      if (formData.isNewProperty) {
        const newPropertyDoc = await addDoc(collection(db, 'properties'), {
          clientId,
          addressLine1: formData.newProperty.street,
          city: formData.newProperty.city,
          state: formData.newProperty.state,
          zip: formData.newProperty.zip,
          createdAt: new Date().toISOString()
        });
        propertyId = newPropertyDoc.id;
        propertyData = formData.newProperty;
      } else if (formData.propertyId) {
        const property = properties.find(p => p.id === formData.propertyId);
        propertyData = {
          street: property?.addressLine1 || property?.street || '',
          city: property?.city || '',
          state: property?.state || '',
          zip: property?.zip || ''
        };
      }

      // Calculate reminder times
      const appointmentDateTime = new Date(`${formData.date}T${formData.time}`);
      const scheduledReminders = [];
      
      Object.entries(formData.reminders).forEach(([key, enabled]) => {
        if (enabled) {
          const reminderOption = reminderOptions.find(r => r.value === key);
          if (reminderOption) {
            const reminderTime = new Date(appointmentDateTime.getTime() - (reminderOption.hours * 60 * 60 * 1000));
            scheduledReminders.push({
              type: key,
              scheduledFor: reminderTime.toISOString(),
              sent: false,
              channels: formData.reminderChannels
            });
          }
        }
      });

      // Create appointment
      const appointmentData = {
        clientId,
        propertyId,
        clientFirstName: clientData.firstName,
        clientLastName: clientData.lastName,
        clientEmail: clientData.email,
        clientPhone: clientData.phone,
        address: propertyData.street ? 
          `${propertyData.street}, ${propertyData.city}, ${propertyData.state} ${propertyData.zip}` : '',
        date: formData.date,
        time: formData.time,
        duration: formData.duration,
        notes: formData.notes,
        internalNotes: formData.internalNotes,
        status: 'scheduled',
        reminders: scheduledReminders,
        reminderChannels: formData.reminderChannels,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const docRef = await addDoc(collection(db, 'onSiteAppointments'), appointmentData);
      
      // Add to local state
      setAppointments(prev => [...prev, { id: docRef.id, ...appointmentData }]);
      
      // Send immediate confirmation if client has contact info
      if (clientData.email || clientData.phone) {
        await sendConfirmation({ id: docRef.id, ...appointmentData });
      }

      setShowNewModal(false);
      resetForm();
    } catch (error) {
      console.error('Error creating appointment:', error);
      alert('Failed to create appointment. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Send confirmation notification
  const sendConfirmation = async (appointment) => {
    try {
      const response = await fetch('/.netlify/functions/send-appointment-reminder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'confirmation',
          appointment: {
            clientName: `${appointment.clientFirstName} ${appointment.clientLastName}`.trim(),
            clientEmail: appointment.clientEmail,
            clientPhone: appointment.clientPhone,
            date: appointment.date,
            time: appointment.time,
            address: appointment.address,
            channels: appointment.reminderChannels
          }
        })
      });
      
      if (!response.ok) {
        console.error('Failed to send confirmation');
      }
    } catch (error) {
      console.error('Error sending confirmation:', error);
    }
  };

  // Update appointment status
  const updateAppointmentStatus = async (appointmentId, newStatus) => {
    try {
      await updateDoc(doc(db, 'onSiteAppointments', appointmentId), {
        status: newStatus,
        updatedAt: new Date().toISOString()
      });
      
      setAppointments(prev => prev.map(apt => 
        apt.id === appointmentId ? { ...apt, status: newStatus } : apt
      ));
      
      if (selectedAppointment?.id === appointmentId) {
        setSelectedAppointment(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  // Delete appointment
  const deleteAppointment = async (appointmentId) => {
    if (!window.confirm('Are you sure you want to delete this appointment?')) return;
    
    try {
      await deleteDoc(doc(db, 'onSiteAppointments', appointmentId));
      setAppointments(prev => prev.filter(apt => apt.id !== appointmentId));
      setShowDetailModal(false);
      setSelectedAppointment(null);
    } catch (error) {
      console.error('Error deleting appointment:', error);
    }
  };

  // Convert to estimate
  const convertToEstimate = (appointment) => {
    navigate(`/admin/estimates/new?clientId=${appointment.clientId}&propertyId=${appointment.propertyId}`);
  };

  // Open appointment detail
  const openAppointmentDetail = (appointment) => {
    setSelectedAppointment(appointment);
    setShowDetailModal(true);
  };

  // Render appointment card
  const AppointmentCard = ({ appointment, compact = false }) => {
    const status = appointmentStatuses[appointment.status] || appointmentStatuses.scheduled;
    const StatusIcon = status.icon;
    
    return (
      <div 
        className={`appointment-card ${compact ? 'compact' : ''}`}
        onClick={() => openAppointmentDetail(appointment)}
        style={{ borderLeftColor: status.color }}
      >
        <div className="apt-header">
          <span className="apt-time">{formatTime(appointment.time)}</span>
          <span className="apt-status" style={{ background: status.bg, color: status.color }}>
            <StatusIcon size={12} />
            {status.label}
          </span>
        </div>
        <div className="apt-client">
          <User size={14} />
          {appointment.clientFirstName} {appointment.clientLastName}
        </div>
        {!compact && appointment.address && (
          <div className="apt-address">
            <MapPin size={14} />
            {appointment.address}
          </div>
        )}
        {!compact && appointment.clientPhone && (
          <div className="apt-phone">
            <Phone size={14} />
            {appointment.clientPhone}
          </div>
        )}
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="on-site-appointments">
        <div className="loading-state">
          <Loader size={32} className="spinning" />
          <p>Loading appointments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="on-site-appointments">
      {/* Header */}
      <div className="page-header">
        <div className="header-title">
          <h1>On-Site Estimates</h1>
          <p>Schedule and manage in-person estimate appointments</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowNewModal(true)}>
          <Plus size={18} />
          New Appointment
        </button>
      </div>

      {/* Filters & Search */}
      <div className="filters-bar">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search by name, address, or phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        <div className="filter-group">
          <select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
            className="status-filter"
          >
            <option value="all">All Statuses</option>
            {Object.entries(appointmentStatuses).map(([key, config]) => (
              <option key={key} value={key}>{config.label}</option>
            ))}
          </select>
          
          <div className="view-toggle">
            <button 
              className={viewMode === 'day' ? 'active' : ''} 
              onClick={() => setViewMode('day')}
            >
              Day
            </button>
            <button 
              className={viewMode === 'week' ? 'active' : ''} 
              onClick={() => setViewMode('week')}
            >
              Week
            </button>
            <button 
              className={viewMode === 'list' ? 'active' : ''} 
              onClick={() => setViewMode('list')}
            >
              List
            </button>
          </div>
        </div>
      </div>

      {/* Calendar Navigation */}
      <div className="calendar-nav">
        <button className="nav-btn" onClick={() => viewMode === 'week' ? navigateWeek(-1) : navigateDay(-1)}>
          <ChevronLeft size={20} />
        </button>
        
        <div className="current-range">
          {viewMode === 'week' ? (
            <span>
              {formatDate(weekDates[0])} - {formatDate(weekDates[6])}
            </span>
          ) : viewMode === 'day' ? (
            <span>{formatDate(selectedDate)}</span>
          ) : (
            <span>All Appointments</span>
          )}
        </div>
        
        <button className="nav-btn" onClick={() => viewMode === 'week' ? navigateWeek(1) : navigateDay(1)}>
          <ChevronRight size={20} />
        </button>
        
        <button className="today-btn" onClick={() => setSelectedDate(new Date())}>
          Today
        </button>
      </div>

      {/* Week View */}
      {viewMode === 'week' && (
        <div className="week-view">
          {weekDates.map((date, index) => {
            const dateStr = date.toISOString().split('T')[0];
            const dayAppointments = filteredAppointments.filter(apt => apt.date === dateStr);
            const isToday = dateStr === new Date().toISOString().split('T')[0];
            
            return (
              <div key={index} className={`day-column ${isToday ? 'today' : ''}`}>
                <div className="day-header">
                  <span className="day-name">{date.toLocaleDateString('en-US', { weekday: 'short' })}</span>
                  <span className="day-number">{date.getDate()}</span>
                </div>
                <div className="day-appointments">
                  {dayAppointments.length === 0 ? (
                    <div className="no-appointments">No appointments</div>
                  ) : (
                    dayAppointments
                      .sort((a, b) => a.time.localeCompare(b.time))
                      .map(apt => (
                        <AppointmentCard key={apt.id} appointment={apt} compact />
                      ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Day View */}
      {viewMode === 'day' && (
        <div className="day-view">
          {timeSlots.filter((_, i) => i % 2 === 0).map(slot => {
            const slotAppointments = filteredAppointments.filter(apt => 
              apt.date === selectedDate.toISOString().split('T')[0] && apt.time === slot.value
            );
            
            return (
              <div key={slot.value} className="time-slot">
                <div className="slot-time">{slot.label}</div>
                <div className="slot-appointments">
                  {slotAppointments.map(apt => (
                    <AppointmentCard key={apt.id} appointment={apt} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div className="list-view">
          {filteredAppointments.length === 0 ? (
            <div className="empty-state">
              <Calendar size={48} />
              <h3>No appointments found</h3>
              <p>Schedule your first on-site estimate appointment.</p>
              <button className="btn btn-primary" onClick={() => setShowNewModal(true)}>
                <Plus size={18} />
                New Appointment
              </button>
            </div>
          ) : (
            <div className="appointments-list">
              {filteredAppointments
                .sort((a, b) => `${a.date}${a.time}`.localeCompare(`${b.date}${b.time}`))
                .map(apt => (
                  <div key={apt.id} className="list-item" onClick={() => openAppointmentDetail(apt)}>
                    <div className="list-date">
                      <span className="date-day">{new Date(apt.date).getDate()}</span>
                      <span className="date-month">{new Date(apt.date).toLocaleDateString('en-US', { month: 'short' })}</span>
                    </div>
                    <div className="list-time">{formatTime(apt.time)}</div>
                    <div className="list-info">
                      <span className="list-client">{apt.clientFirstName} {apt.clientLastName}</span>
                      <span className="list-address">{apt.address}</span>
                    </div>
                    <div className="list-status" style={{ background: appointmentStatuses[apt.status]?.bg, color: appointmentStatuses[apt.status]?.color }}>
                      {appointmentStatuses[apt.status]?.label}
                    </div>
                    <div className="list-actions">
                      <button className="btn-icon" onClick={(e) => { e.stopPropagation(); convertToEstimate(apt); }} title="Create Estimate">
                        <FileText size={16} />
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      {/* New Appointment Modal */}
      <Modal isOpen={showNewModal} onClose={() => { setShowNewModal(false); resetForm(); }} title="Schedule On-Site Estimate">
        <div className="appointment-form">
          {/* Client Selection */}
          <div className="form-section">
            <h3><User size={16} /> Client Information</h3>
            
            <div className="toggle-row">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={formData.isNewClient}
                  onChange={(e) => updateForm('isNewClient', e.target.checked)}
                />
                <span>New Client</span>
              </label>
            </div>

            {formData.isNewClient ? (
              <div className="form-grid">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    value={formData.newClient.firstName}
                    onChange={(e) => updateNewClient('firstName', e.target.value)}
                    placeholder="Jane"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    value={formData.newClient.lastName}
                    onChange={(e) => updateNewClient('lastName', e.target.value)}
                    placeholder="Smith"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.newClient.email}
                    onChange={(e) => updateNewClient('email', e.target.value)}
                    placeholder="jane@email.com"
                  />
                </div>
                <div className="form-group">
                  <label>Phone *</label>
                  <input
                    type="tel"
                    value={formData.newClient.phone}
                    onChange={(e) => updateNewClient('phone', e.target.value)}
                    placeholder="(434) 555-1234"
                    required
                  />
                </div>
              </div>
            ) : (
              <div className="form-group">
                <label>Select Client *</label>
                <select
                  value={formData.clientId}
                  onChange={(e) => {
                    updateForm('clientId', e.target.value);
                    updateForm('propertyId', '');
                  }}
                  required
                >
                  <option value="">Choose a client...</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.firstName} {client.lastName} {client.email ? `(${client.email})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Property Selection */}
          <div className="form-section">
            <h3><MapPin size={16} /> Property Address</h3>
            
            <div className="toggle-row">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={formData.isNewProperty}
                  onChange={(e) => updateForm('isNewProperty', e.target.checked)}
                />
                <span>New Property Address</span>
              </label>
            </div>

            {formData.isNewProperty || formData.isNewClient ? (
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Street Address *</label>
                  <input
                    type="text"
                    value={formData.newProperty.street}
                    onChange={(e) => updateNewProperty('street', e.target.value)}
                    placeholder="123 Main Street"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>City *</label>
                  <input
                    type="text"
                    value={formData.newProperty.city}
                    onChange={(e) => updateNewProperty('city', e.target.value)}
                    required
                  />
                </div>
                <div className="form-group half">
                  <label>State</label>
                  <input
                    type="text"
                    value={formData.newProperty.state}
                    onChange={(e) => updateNewProperty('state', e.target.value)}
                    maxLength={2}
                  />
                </div>
                <div className="form-group half">
                  <label>ZIP *</label>
                  <input
                    type="text"
                    value={formData.newProperty.zip}
                    onChange={(e) => updateNewProperty('zip', e.target.value)}
                    placeholder="22901"
                    required
                  />
                </div>
              </div>
            ) : (
              <div className="form-group">
                <label>Select Property</label>
                <select
                  value={formData.propertyId}
                  onChange={(e) => updateForm('propertyId', e.target.value)}
                  disabled={!formData.clientId}
                >
                  <option value="">Choose a property...</option>
                  {getClientProperties(formData.clientId).map(prop => (
                    <option key={prop.id} value={prop.id}>
                      {prop.addressLine1 || prop.street}, {prop.city}
                    </option>
                  ))}
                </select>
                {formData.clientId && getClientProperties(formData.clientId).length === 0 && (
                  <p className="form-hint">No properties found. Enable "New Property Address" above.</p>
                )}
              </div>
            )}
          </div>

          {/* Date & Time */}
          <div className="form-section">
            <h3><Calendar size={16} /> Appointment Details</h3>
            
            <div className="form-grid">
              <div className="form-group">
                <label>Date *</label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => updateForm('date', e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>
              <div className="form-group">
                <label>Time *</label>
                <select
                  value={formData.time}
                  onChange={(e) => updateForm('time', e.target.value)}
                  required
                >
                  {timeSlots.map(slot => (
                    <option key={slot.value} value={slot.value}>{slot.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Duration</label>
                <select
                  value={formData.duration}
                  onChange={(e) => updateForm('duration', parseInt(e.target.value))}
                >
                  {durationOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Reminders */}
          <div className="form-section">
            <h3><Bell size={16} /> Automatic Reminders</h3>
            <p className="section-note">Client will receive reminders via selected channels</p>
            
            <div className="reminder-channels">
              <label className="channel-toggle">
                <input
                  type="checkbox"
                  checked={formData.reminderChannels.email}
                  onChange={() => toggleReminderChannel('email')}
                />
                <Mail size={16} />
                <span>Email</span>
              </label>
              <label className="channel-toggle">
                <input
                  type="checkbox"
                  checked={formData.reminderChannels.sms}
                  onChange={() => toggleReminderChannel('sms')}
                />
                <MessageSquare size={16} />
                <span>Text Message</span>
              </label>
            </div>
            
            <div className="reminder-options">
              {reminderOptions.map(opt => (
                <label key={opt.value} className="reminder-option">
                  <input
                    type="checkbox"
                    checked={formData.reminders[opt.value]}
                    onChange={() => toggleReminder(opt.value)}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="form-section">
            <h3><FileText size={16} /> Notes</h3>
            
            <div className="form-group">
              <label>Notes for Client (included in reminders)</label>
              <textarea
                value={formData.notes}
                onChange={(e) => updateForm('notes', e.target.value)}
                placeholder="e.g., Please have pets secured, I'll text when I'm on my way..."
                rows={2}
              />
            </div>
            
            <div className="form-group">
              <label>Internal Notes (staff only)</label>
              <textarea
                value={formData.internalNotes}
                onChange={(e) => updateForm('internalNotes', e.target.value)}
                placeholder="e.g., Referred by Smith family, interested in weekly service..."
                rows={2}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => { setShowNewModal(false); resetForm(); }}>
              Cancel
            </button>
            <button 
              type="button" 
              className="btn btn-primary" 
              onClick={handleCreateAppointment}
              disabled={isSaving || (!formData.clientId && !formData.isNewClient)}
            >
              {isSaving ? <Loader size={18} className="spinning" /> : <Check size={18} />}
              {isSaving ? 'Scheduling...' : 'Schedule Appointment'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Appointment Detail Modal */}
      <Modal 
        isOpen={showDetailModal} 
        onClose={() => { setShowDetailModal(false); setSelectedAppointment(null); }} 
        title="Appointment Details"
      >
        {selectedAppointment && (
          <div className="appointment-detail">
            {/* Status Banner */}
            <div 
              className="status-banner"
              style={{ 
                background: appointmentStatuses[selectedAppointment.status]?.bg,
                color: appointmentStatuses[selectedAppointment.status]?.color
              }}
            >
              {React.createElement(appointmentStatuses[selectedAppointment.status]?.icon || Calendar, { size: 20 })}
              <span>{appointmentStatuses[selectedAppointment.status]?.label}</span>
            </div>

            {/* Date & Time */}
            <div className="detail-section">
              <div className="detail-row">
                <Calendar size={18} />
                <div>
                  <strong>{new Date(selectedAppointment.date).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}</strong>
                  <span>{formatTime(selectedAppointment.time)} ({selectedAppointment.duration} min)</span>
                </div>
              </div>
            </div>

            {/* Client Info */}
            <div className="detail-section">
              <h4>Client</h4>
              <div className="detail-row">
                <User size={18} />
                <span>{selectedAppointment.clientFirstName} {selectedAppointment.clientLastName}</span>
              </div>
              {selectedAppointment.clientPhone && (
                <div className="detail-row clickable">
                  <Phone size={18} />
                  <a href={`tel:${selectedAppointment.clientPhone}`}>{selectedAppointment.clientPhone}</a>
                </div>
              )}
              {selectedAppointment.clientEmail && (
                <div className="detail-row clickable">
                  <Mail size={18} />
                  <a href={`mailto:${selectedAppointment.clientEmail}`}>{selectedAppointment.clientEmail}</a>
                </div>
              )}
            </div>

            {/* Address */}
            {selectedAppointment.address && (
              <div className="detail-section">
                <h4>Location</h4>
                <div className="detail-row clickable">
                  <MapPin size={18} />
                  <a 
                    href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(selectedAppointment.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {selectedAppointment.address}
                    <ExternalLink size={14} />
                  </a>
                </div>
              </div>
            )}

            {/* Reminders */}
            <div className="detail-section">
              <h4>Reminders</h4>
              <div className="reminders-list">
                {selectedAppointment.reminders?.map((reminder, index) => (
                  <div key={index} className={`reminder-item ${reminder.sent ? 'sent' : 'pending'}`}>
                    <Bell size={14} />
                    <span>{reminderOptions.find(r => r.value === reminder.type)?.label || reminder.type}</span>
                    <span className="reminder-status">
                      {reminder.sent ? (
                        <><CheckCircle size={14} /> Sent</>
                      ) : (
                        <><Clock size={14} /> Scheduled</>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Notes */}
            {(selectedAppointment.notes || selectedAppointment.internalNotes) && (
              <div className="detail-section">
                <h4>Notes</h4>
                {selectedAppointment.notes && (
                  <div className="note-block">
                    <span className="note-label">Client-Facing:</span>
                    <p>{selectedAppointment.notes}</p>
                  </div>
                )}
                {selectedAppointment.internalNotes && (
                  <div className="note-block internal">
                    <span className="note-label">Internal:</span>
                    <p>{selectedAppointment.internalNotes}</p>
                  </div>
                )}
              </div>
            )}

            {/* Status Actions */}
            <div className="detail-section">
              <h4>Update Status</h4>
              <div className="status-buttons">
                {Object.entries(appointmentStatuses).map(([key, config]) => (
                  <button
                    key={key}
                    className={`status-btn ${selectedAppointment.status === key ? 'active' : ''}`}
                    style={{ 
                      background: selectedAppointment.status === key ? config.color : config.bg,
                      color: selectedAppointment.status === key ? 'white' : config.color
                    }}
                    onClick={() => updateAppointmentStatus(selectedAppointment.id, key)}
                  >
                    {config.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="detail-actions">
              <button 
                className="btn btn-danger-outline"
                onClick={() => deleteAppointment(selectedAppointment.id)}
              >
                <Trash2 size={18} />
                Delete
              </button>
              <button 
                className="btn btn-primary"
                onClick={() => convertToEstimate(selectedAppointment)}
              >
                <FileText size={18} />
                Create Estimate
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default OnSiteAppointments;
